import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import {
  getExtendedStats,
  getAdminUsers,
  updateUserRole,
  getAdminEvents,
  createAdminEvent,
  deleteAdminEvent,
  getAdminQuests,
  createAdminQuest,
  updateAdminQuest,
  deleteAdminQuest,
  getAdminGuilds,
  createAdminGuild,
  updateAdminGuild,
  deleteAdminGuild,
  getNotifications,
  sendNotification,
  type ExtendedStats,
  type AdminUser,
  type AdminEvent,
  type AdminQuest,
  type AdminGuild,
  type AdminNotification,
} from '@/services/admin';

interface AdminPageProps {
  onClose: () => void;
}

type AdminTab = 'quests' | 'events' | 'users' | 'stats' | 'guilds' | 'notifications';

const ROLE_OPTIONS = ['user', 'admin', 'event_manager', 'partner_organizer'];
const ROLE_LABELS: Record<string, string> = {
  user: 'Юзер',
  admin: 'Админ',
  event_manager: 'Ивент-менеджер',
  partner_organizer: 'Партнёр',
};

const QUEST_TYPES = ['daily', 'weekly', 'story', 'challenge', 'social'];
const QUEST_TYPE_LABELS: Record<string, string> = {
  daily: 'Ежедневный',
  weekly: 'Недельный',
  story: 'Сюжетный',
  challenge: 'Челлендж',
  social: 'Социальный',
};

const EVENT_FORMATS = ['online', 'offline', 'hybrid'];
const EVENT_FORMAT_LABELS: Record<string, string> = {
  online: 'Онлайн',
  offline: 'Офлайн',
  hybrid: 'Гибрид',
};

const EVENT_STATUSES = ['draft', 'published'];
const EVENT_STATUS_LABELS: Record<string, string> = {
  draft: 'Черновик',
  published: 'Опубликовано',
};

export function AdminPage({ onClose }: AdminPageProps) {
  const [activeTab, setActiveTab] = useState<AdminTab>('stats');

  const tabs: { id: AdminTab; label: string; emoji: string }[] = [
    { id: 'quests', label: 'Квесты', emoji: '🎯' },
    { id: 'events', label: 'События', emoji: '📅' },
    { id: 'users', label: 'Юзеры', emoji: '👥' },
    { id: 'stats', label: 'Стата', emoji: '📊' },
    { id: 'guilds', label: 'Гильдии', emoji: '🏰' },
    { id: 'notifications', label: 'Рассылка', emoji: '📢' },
  ];

  return (
    <div className="min-h-screen bg-primary-950 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-primary-800/50">
        <button onClick={onClose} className="text-primary-400">
          ← Назад
        </button>
        <h1 className="text-lg font-semibold text-white">Админ-панель</h1>
        <div className="w-16" />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-primary-800/50 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 py-2 px-3 flex flex-col items-center gap-0.5 transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-800/50 text-white'
                : 'text-primary-500'
            }`}
          >
            <span className="text-base">{tab.emoji}</span>
            <span className="text-[10px]">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'stats' && <StatsTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'events' && <EventsTab />}
        {activeTab === 'quests' && <QuestsTab />}
        {activeTab === 'guilds' && <GuildsTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
      </div>
    </div>
  );
}

// ============================================
// STATS TAB (Extended)
// ============================================

function StatsTab() {
  const [stats, setStats] = useState<ExtendedStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getExtendedStats();
        setStats(data);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
        {error}
      </div>
    );
  }

  if (!stats) return null;

  const mainCards: { label: string; value: string | number; emoji: string }[] = [
    { label: 'Всего юзеров', value: stats.totalUsers, emoji: '👤' },
    { label: 'Онбординг', value: `${stats.onboardingCompleted} (${stats.onboardingRate}%)`, emoji: '✅' },
    { label: 'Активных за 7д', value: stats.usersActiveThisWeek, emoji: '🔥' },
    { label: 'Тест B', value: stats.batteryBCompleted, emoji: '🔬' },
    { label: 'Тест C', value: stats.batteryCCompleted, emoji: '⚡' },
    { label: 'Событий', value: `${stats.activeEvents}/${stats.totalEvents}`, emoji: '📅' },
    { label: 'Квестов', value: `${stats.activeQuests}/${stats.totalQuests}`, emoji: '🎯' },
    { label: 'Гильдий', value: stats.totalGuilds, emoji: '🏰' },
    { label: 'Записей летописи', value: stats.totalChronicleEntries, emoji: '📜' },
    { label: 'Записей дневника', value: stats.totalJournalEntries, emoji: '📝' },
  ];

  return (
    <div className="space-y-6">
      {/* Main stats grid */}
      <div className="grid grid-cols-2 gap-3">
        {mainCards.map(card => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-primary-800/50 rounded-xl p-3"
          >
            <span className="text-xl">{card.emoji}</span>
            <p className="text-lg font-bold text-white mt-1">{card.value}</p>
            <p className="text-[10px] text-primary-400">{card.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Registrations chart */}
      <div className="bg-primary-800/50 rounded-xl p-4">
        <h3 className="text-sm font-medium text-white mb-3">📈 Регистрации за 7 дней</h3>
        <div className="flex items-end gap-1 h-20">
          {stats.registrationsLast7Days.map((count, i) => {
            const max = Math.max(...stats.registrationsLast7Days, 1);
            const height = (count / max) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-accent-teal/50 rounded-t"
                  style={{ height: `${height}%`, minHeight: count > 0 ? '4px' : '0' }}
                />
                <span className="text-[9px] text-primary-500">{count}</span>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[9px] text-primary-600 mt-1">
          <span>7д назад</span>
          <span>Сегодня</span>
        </div>
      </div>

      {/* Users by archetype */}
      {Object.keys(stats.usersByArchetype).length > 0 && (
        <div className="bg-primary-800/50 rounded-xl p-4">
          <h3 className="text-sm font-medium text-white mb-3">🎭 По архетипам</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.usersByArchetype).map(([archetype, count]) => (
              <span key={archetype} className="text-xs px-2 py-1 bg-primary-700/50 rounded text-primary-300">
                {archetype}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Users by stage */}
      <div className="bg-primary-800/50 rounded-xl p-4">
        <h3 className="text-sm font-medium text-white mb-3">🛤️ По этапам</h3>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5, 6, 7].map(stage => (
            <div key={stage} className="flex-1 text-center">
              <div className="text-lg font-bold text-white">{stats.usersByStage[String(stage)] || 0}</div>
              <div className="text-[10px] text-primary-500">Этап {stage}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// USERS TAB
// ============================================

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editingUser, setEditingUser] = useState<string | null>(null);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await getAdminUsers({
        search: search || undefined,
        role: roleFilter || undefined,
        limit: 50,
      });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [roleFilter]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await updateUserRole(userId, newRole);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setEditingUser(null);
    } catch (e: any) {
      alert(e.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex gap-2">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && loadUsers()}
          placeholder="Поиск..."
          className="flex-1 px-3 py-2 bg-primary-800/50 border border-primary-700/50 rounded-lg text-white text-sm placeholder-primary-500 outline-none focus:border-primary-500"
        />
        <Button onClick={loadUsers} variant="ghost" size="sm">
          Найти
        </Button>
      </div>

      {/* Role filter */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setRoleFilter('')}
          className={`px-2 py-1 rounded-full text-xs ${
            !roleFilter ? 'bg-primary-500 text-white' : 'bg-primary-800/50 text-primary-400'
          }`}
        >
          Все
        </button>
        {ROLE_OPTIONS.map(r => (
          <button
            key={r}
            onClick={() => setRoleFilter(r)}
            className={`px-2 py-1 rounded-full text-xs ${
              roleFilter === r ? 'bg-primary-500 text-white' : 'bg-primary-800/50 text-primary-400'
            }`}
          >
            {ROLE_LABELS[r]}
          </button>
        ))}
      </div>

      <p className="text-xs text-primary-500">Всего: {total}</p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(user => (
            <div key={user.id} className="bg-primary-800/50 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">
                    {user.firstName || user.username || `#${user.telegramId}`}
                  </p>
                  <p className="text-xs text-primary-400">
                    {user.primaryArchetype || '—'} | Этап {user.currentStage}
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  user.role === 'admin' ? 'bg-red-500/20 text-red-400' :
                  user.role === 'event_manager' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-primary-700/50 text-primary-400'
                }`}>
                  {ROLE_LABELS[user.role] || user.role}
                </span>
              </div>

              <div className="flex gap-1 flex-wrap">
                {user.batteryACompleted && <span className="text-xs px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">A</span>}
                {user.batteryBCompleted && <span className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">B</span>}
                {user.batteryCCompleted && <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded">C</span>}
              </div>

              {editingUser === user.id ? (
                <div className="flex gap-1 flex-wrap">
                  {ROLE_OPTIONS.map(r => (
                    <button
                      key={r}
                      onClick={() => handleRoleChange(user.id, r)}
                      className={`px-2 py-1 rounded text-xs ${
                        user.role === r ? 'bg-primary-500 text-white' : 'bg-primary-700/50 text-primary-300'
                      }`}
                    >
                      {ROLE_LABELS[r]}
                    </button>
                  ))}
                  <button onClick={() => setEditingUser(null)} className="px-2 py-1 rounded text-xs text-primary-500">
                    ✕
                  </button>
                </div>
              ) : (
                <button onClick={() => setEditingUser(user.id)} className="text-xs text-primary-500 underline">
                  Изменить роль
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// EVENTS TAB
// ============================================

function EventsTab() {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create form state
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    format: 'online',
    status: 'draft',
    startsAt: '',
    location: '',
    maxParticipants: 0,
    xpReward: 50,
  });

  const loadEvents = async () => {
    setIsLoading(true);
    try {
      const data = await getAdminEvents({ status: statusFilter || undefined });
      setEvents(data.events);
    } catch (e) {
      console.error('Failed to load events:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [statusFilter]);

  const handleCreate = async () => {
    if (!newEvent.title || !newEvent.startsAt) {
      alert('Заполни название и дату начала');
      return;
    }
    setCreating(true);
    try {
      await createAdminEvent({
        ...newEvent,
        startsAt: new Date(newEvent.startsAt).toISOString(),
        maxParticipants: newEvent.maxParticipants || null,
      });
      setShowCreate(false);
      setNewEvent({
        title: '',
        description: '',
        format: 'online',
        status: 'draft',
        startsAt: '',
        location: '',
        maxParticipants: 0,
        xpReward: 50,
      });
      loadEvents();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (eventId: string) => {
    if (!confirm('Удалить событие?')) return;
    try {
      await deleteAdminEvent(eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
    } catch (e: any) {
      alert(e.message);
    }
  };

  const statusOptions = ['', 'draft', 'published', 'cancelled', 'completed'];
  const statusLabels: Record<string, string> = {
    '': 'Все',
    draft: 'Черновик',
    published: 'Опубл.',
    cancelled: 'Отменено',
    completed: 'Завершено',
  };

  return (
    <div className="space-y-4">
      {/* Create button */}
      <Button onClick={() => setShowCreate(!showCreate)} variant="secondary" size="sm" fullWidth>
        {showCreate ? 'Скрыть форму' : '+ Создать событие'}
      </Button>

      {/* Create form */}
      {showCreate && (
        <div className="bg-primary-800/50 rounded-xl p-4 space-y-3">
          <input
            type="text"
            placeholder="Название *"
            value={newEvent.title}
            onChange={e => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
          />
          <textarea
            placeholder="Описание"
            value={newEvent.description}
            onChange={e => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none resize-none"
            rows={2}
          />
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-primary-400 block mb-1">Дата и время *</label>
              <input
                type="datetime-local"
                value={newEvent.startsAt}
                onChange={e => setNewEvent(prev => ({ ...prev, startsAt: e.target.value }))}
                className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={newEvent.format}
              onChange={e => setNewEvent(prev => ({ ...prev, format: e.target.value }))}
              className="flex-1 px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
            >
              {EVENT_FORMATS.map(f => (
                <option key={f} value={f}>{EVENT_FORMAT_LABELS[f]}</option>
              ))}
            </select>
            <select
              value={newEvent.status}
              onChange={e => setNewEvent(prev => ({ ...prev, status: e.target.value }))}
              className="flex-1 px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
            >
              {EVENT_STATUSES.map(s => (
                <option key={s} value={s}>{EVENT_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Место проведения"
            value={newEvent.location}
            onChange={e => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
          />
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-primary-400 block mb-1">Макс. участников</label>
              <input
                type="number"
                placeholder="0 = без лимита"
                value={newEvent.maxParticipants || ''}
                onChange={e => setNewEvent(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-primary-400 block mb-1">XP награда</label>
              <input
                type="number"
                placeholder="XP"
                value={newEvent.xpReward}
                onChange={e => setNewEvent(prev => ({ ...prev, xpReward: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
              />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={creating} fullWidth>
            {creating ? 'Создание...' : 'Создать'}
          </Button>
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {statusOptions.map(s => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={`px-2 py-1 rounded-full text-xs ${
              statusFilter === s ? 'bg-primary-500 text-white' : 'bg-primary-800/50 text-primary-400'
            }`}
          >
            {statusLabels[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-8">
          <span className="text-4xl block mb-2">📅</span>
          <p className="text-primary-400">Нет событий</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(event => {
            const dateStr = new Date(event.startsAt).toLocaleDateString('ru-RU', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            });

            return (
              <div key={event.id} className="bg-primary-800/50 rounded-xl p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{event.title}</p>
                    <p className="text-xs text-primary-400 mt-1">
                      {dateStr} | {event.format || '—'} | XP: {event.xpReward}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      event.status === 'published' ? 'bg-green-500/20 text-green-400' :
                      event.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                      event.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                      'bg-primary-700/50 text-primary-400'
                    }`}>
                      {statusLabels[event.status] || event.status}
                    </span>
                    <button onClick={() => handleDelete(event.id)} className="text-red-400 text-xs">✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// QUESTS TAB
// ============================================

function QuestsTab() {
  const [quests, setQuests] = useState<AdminQuest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create form state
  const [newQuest, setNewQuest] = useState({
    questKey: '',
    title: '',
    description: '',
    questType: 'daily',
    xpReward: 50,
  });

  const loadQuests = async () => {
    setIsLoading(true);
    try {
      const data = await getAdminQuests({ questType: typeFilter || undefined });
      setQuests(data.quests);
    } catch (e) {
      console.error('Failed to load quests:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadQuests();
  }, [typeFilter]);

  const handleCreate = async () => {
    if (!newQuest.questKey || !newQuest.title) {
      alert('Заполни questKey и title');
      return;
    }
    setCreating(true);
    try {
      await createAdminQuest(newQuest);
      setShowCreate(false);
      setNewQuest({ questKey: '', title: '', description: '', questType: 'daily', xpReward: 50 });
      loadQuests();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (quest: AdminQuest) => {
    try {
      await updateAdminQuest(quest.id, { isActive: !quest.isActive });
      setQuests(prev => prev.map(q => q.id === quest.id ? { ...q, isActive: !q.isActive } : q));
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleDelete = async (questId: string) => {
    if (!confirm('Удалить квест?')) return;
    try {
      await deleteAdminQuest(questId);
      setQuests(prev => prev.filter(q => q.id !== questId));
    } catch (e: any) {
      alert(e.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Create button */}
      <Button onClick={() => setShowCreate(!showCreate)} variant="secondary" size="sm" fullWidth>
        {showCreate ? 'Скрыть форму' : '+ Создать квест'}
      </Button>

      {/* Create form */}
      {showCreate && (
        <div className="bg-primary-800/50 rounded-xl p-4 space-y-3">
          <input
            type="text"
            placeholder="questKey (уникальный)"
            value={newQuest.questKey}
            onChange={e => setNewQuest(prev => ({ ...prev, questKey: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
          />
          <input
            type="text"
            placeholder="Название"
            value={newQuest.title}
            onChange={e => setNewQuest(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
          />
          <textarea
            placeholder="Описание"
            value={newQuest.description}
            onChange={e => setNewQuest(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none resize-none"
            rows={2}
          />
          <div className="flex gap-2">
            <select
              value={newQuest.questType}
              onChange={e => setNewQuest(prev => ({ ...prev, questType: e.target.value }))}
              className="flex-1 px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
            >
              {QUEST_TYPES.map(t => (
                <option key={t} value={t}>{QUEST_TYPE_LABELS[t]}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="XP"
              value={newQuest.xpReward}
              onChange={e => setNewQuest(prev => ({ ...prev, xpReward: parseInt(e.target.value) || 0 }))}
              className="w-20 px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
            />
          </div>
          <Button onClick={handleCreate} disabled={creating} fullWidth>
            {creating ? 'Создание...' : 'Создать'}
          </Button>
        </div>
      )}

      {/* Type filter */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setTypeFilter('')}
          className={`px-2 py-1 rounded-full text-xs ${
            !typeFilter ? 'bg-primary-500 text-white' : 'bg-primary-800/50 text-primary-400'
          }`}
        >
          Все
        </button>
        {QUEST_TYPES.map(t => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={`px-2 py-1 rounded-full text-xs ${
              typeFilter === t ? 'bg-primary-500 text-white' : 'bg-primary-800/50 text-primary-400'
            }`}
          >
            {QUEST_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : quests.length === 0 ? (
        <div className="text-center py-8">
          <span className="text-4xl block mb-2">🎯</span>
          <p className="text-primary-400">Нет квестов</p>
        </div>
      ) : (
        <div className="space-y-2">
          {quests.map(quest => (
            <div key={quest.id} className="bg-primary-800/50 rounded-xl p-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{quest.title}</p>
                  <p className="text-xs text-primary-400 mt-1">
                    {QUEST_TYPE_LABELS[quest.questType]} | XP: {quest.xpReward} | Участников: {quest.participantsCount || 0} ({quest.completedCount || 0} ✓)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(quest)}
                    className={`text-xs px-2 py-0.5 rounded ${
                      quest.isActive ? 'bg-green-500/20 text-green-400' : 'bg-primary-700/50 text-primary-400'
                    }`}
                  >
                    {quest.isActive ? 'Активен' : 'Выкл'}
                  </button>
                  <button onClick={() => handleDelete(quest.id)} className="text-red-400 text-xs">✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// GUILDS TAB
// ============================================

function GuildsTab() {
  const [guilds, setGuilds] = useState<AdminGuild[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [newGuild, setNewGuild] = useState({
    name: '',
    description: '',
    emoji: '',
    city: '',
    telegramChatLink: '',
  });

  const loadGuilds = async () => {
    setIsLoading(true);
    try {
      const data = await getAdminGuilds();
      setGuilds(data.guilds);
    } catch (e) {
      console.error('Failed to load guilds:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGuilds();
  }, []);

  const handleCreate = async () => {
    if (!newGuild.name) {
      alert('Укажи название');
      return;
    }
    setCreating(true);
    try {
      await createAdminGuild(newGuild);
      setShowCreate(false);
      setNewGuild({ name: '', description: '', emoji: '', city: '', telegramChatLink: '' });
      loadGuilds();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (guild: AdminGuild) => {
    try {
      await updateAdminGuild(guild.id, { isActive: !guild.isActive });
      setGuilds(prev => prev.map(g => g.id === guild.id ? { ...g, isActive: !g.isActive } : g));
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleDelete = async (guildId: string) => {
    if (!confirm('Удалить гильдию?')) return;
    try {
      await deleteAdminGuild(guildId);
      setGuilds(prev => prev.filter(g => g.id !== guildId));
    } catch (e: any) {
      alert(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setShowCreate(!showCreate)} variant="secondary" size="sm" fullWidth>
        {showCreate ? 'Скрыть форму' : '+ Создать гильдию'}
      </Button>

      {showCreate && (
        <div className="bg-primary-800/50 rounded-xl p-4 space-y-3">
          <input
            type="text"
            placeholder="Название"
            value={newGuild.name}
            onChange={e => setNewGuild(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
          />
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Эмодзи"
              value={newGuild.emoji}
              onChange={e => setNewGuild(prev => ({ ...prev, emoji: e.target.value }))}
              className="w-16 px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
            />
            <input
              type="text"
              placeholder="Город"
              value={newGuild.city}
              onChange={e => setNewGuild(prev => ({ ...prev, city: e.target.value }))}
              className="flex-1 px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
            />
          </div>
          <textarea
            placeholder="Описание"
            value={newGuild.description}
            onChange={e => setNewGuild(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none resize-none"
            rows={2}
          />
          <input
            type="text"
            placeholder="Ссылка на TG чат"
            value={newGuild.telegramChatLink}
            onChange={e => setNewGuild(prev => ({ ...prev, telegramChatLink: e.target.value }))}
            className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
          />
          <Button onClick={handleCreate} disabled={creating} fullWidth>
            {creating ? 'Создание...' : 'Создать'}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : guilds.length === 0 ? (
        <div className="text-center py-8">
          <span className="text-4xl block mb-2">🏰</span>
          <p className="text-primary-400">Нет гильдий</p>
        </div>
      ) : (
        <div className="space-y-2">
          {guilds.map(guild => (
            <div key={guild.id} className="bg-primary-800/50 rounded-xl p-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">
                    {guild.emoji && `${guild.emoji} `}{guild.name}
                  </p>
                  <p className="text-xs text-primary-400 mt-1">
                    {guild.city || 'Без города'} | {guild.memberCount} участников
                  </p>
                  {guild.telegramChatLink && (
                    <a
                      href={guild.telegramChatLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-accent-teal underline"
                    >
                      TG чат
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(guild)}
                    className={`text-xs px-2 py-0.5 rounded ${
                      guild.isActive ? 'bg-green-500/20 text-green-400' : 'bg-primary-700/50 text-primary-400'
                    }`}
                  >
                    {guild.isActive ? 'Активна' : 'Выкл'}
                  </button>
                  <button onClick={() => handleDelete(guild.id)} className="text-red-400 text-xs">✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// NOTIFICATIONS TAB
// ============================================

function NotificationsTab() {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [newNotif, setNewNotif] = useState({
    title: '',
    message: '',
    target: 'all',
  });

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const data = await getNotifications(30);
      setNotifications(data.notifications);
    } catch (e) {
      console.error('Failed to load notifications:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleSend = async () => {
    if (!newNotif.message) {
      alert('Введи сообщение');
      return;
    }
    if (!confirm(`Отправить уведомление? Цель: ${newNotif.target}`)) return;

    setSending(true);
    try {
      const result = await sendNotification(newNotif);
      alert(`Отправлено! Получателей: ${result.recipientCount}`);
      setNewNotif({ title: '', message: '', target: 'all' });
      loadNotifications();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSending(false);
    }
  };

  const targetOptions = [
    { value: 'all', label: 'Всем юзерам' },
    { value: 'archetype:HER', label: 'Архетип: Герой' },
    { value: 'archetype:SAG', label: 'Архетип: Мудрец' },
    { value: 'archetype:CRE', label: 'Архетип: Творец' },
  ];

  return (
    <div className="space-y-4">
      {/* Send form */}
      <div className="bg-primary-800/50 rounded-xl p-4 space-y-3">
        <h3 className="text-sm font-medium text-white">📢 Новая рассылка</h3>
        <input
          type="text"
          placeholder="Заголовок (опционально)"
          value={newNotif.title}
          onChange={e => setNewNotif(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
        />
        <textarea
          placeholder="Сообщение *"
          value={newNotif.message}
          onChange={e => setNewNotif(prev => ({ ...prev, message: e.target.value }))}
          className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none resize-none"
          rows={3}
        />
        <select
          value={newNotif.target}
          onChange={e => setNewNotif(prev => ({ ...prev, target: e.target.value }))}
          className="w-full px-3 py-2 bg-primary-700/50 border border-primary-600/50 rounded-lg text-white text-sm outline-none"
        >
          {targetOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <Button onClick={handleSend} disabled={sending} fullWidth>
          {sending ? 'Отправка...' : '📤 Отправить'}
        </Button>
      </div>

      {/* History */}
      <h3 className="text-sm font-medium text-white">История рассылок</h3>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-8">
          <span className="text-4xl block mb-2">📭</span>
          <p className="text-primary-400">Нет рассылок</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map(notif => {
            const dateStr = new Date(notif.sentAt).toLocaleDateString('ru-RU', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            });

            return (
              <div key={notif.id} className="bg-primary-800/50 rounded-xl p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {notif.title && <p className="text-sm font-medium text-white">{notif.title}</p>}
                    <p className="text-xs text-primary-300 mt-1 line-clamp-2">{notif.message}</p>
                    <p className="text-xs text-primary-500 mt-1">
                      {dateStr} | {notif.target} | {notif.recipientCount} получателей
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
